/**
 * Shared json-render catalog for the Taqat platform.
 * Defines all available components (shadcn + custom domain) and actions
 * that the AI and onboarding wizard can use to compose UI.
 *
 * @module lib/json-render/catalog
 */

import { schema } from '@json-render/react'
import { shadcnComponentDefinitions } from '@json-render/shadcn/catalog'
import { z } from 'zod'

/**
 * Taqat component catalog combining shadcn/ui primitives with
 * domain-specific components for compliance, onboarding, and chat.
 */
export const taqatCatalog = schema.createCatalog({
  components: {
    // ── shadcn/ui layout ──────────────────────────────────
    Card: shadcnComponentDefinitions.Card,
    Stack: shadcnComponentDefinitions.Stack,
    Grid: shadcnComponentDefinitions.Grid,
    Separator: shadcnComponentDefinitions.Separator,
    Tabs: shadcnComponentDefinitions.Tabs,
    Accordion: shadcnComponentDefinitions.Accordion,

    // ── shadcn/ui display ─────────────────────────────────
    Heading: shadcnComponentDefinitions.Heading,
    Text: shadcnComponentDefinitions.Text,
    Badge: shadcnComponentDefinitions.Badge,
    Alert: shadcnComponentDefinitions.Alert,
    Progress: shadcnComponentDefinitions.Progress,
    Avatar: shadcnComponentDefinitions.Avatar,
    Image: shadcnComponentDefinitions.Image,

    // ── shadcn/ui form ────────────────────────────────────
    Input: shadcnComponentDefinitions.Input,
    Textarea: shadcnComponentDefinitions.Textarea,
    Select: shadcnComponentDefinitions.Select,
    Checkbox: shadcnComponentDefinitions.Checkbox,
    Radio: shadcnComponentDefinitions.Radio,
    Switch: shadcnComponentDefinitions.Switch,
    Slider: shadcnComponentDefinitions.Slider,
    Button: shadcnComponentDefinitions.Button,
    ButtonGroup: shadcnComponentDefinitions.ButtonGroup,

    // ── Custom domain: compliance findings ────────────────
    FindingCard: {
      props: z.object({
        title: z.string().describe('Finding title'),
        status: z.enum(['compliant', 'partial', 'non-compliant']).describe('Compliance status'),
        control: z.string().describe('Control reference (e.g. A.5.1)'),
        priority: z.enum(['high', 'medium', 'low']).describe('Priority level'),
        effort: z.string().nullable().describe('Estimated remediation effort'),
        description: z.string().describe('Finding description'),
        recommendation: z.string().describe('Recommended action'),
      }),
      description:
        'Displays a compliance finding card with color-coded status, control reference, and recommendation.',
      example: {
        title: 'Access Control Policy',
        status: 'partial',
        control: 'A.5.1',
        priority: 'high',
        description: 'Access control policy needs updating.',
        recommendation: 'Review and update the access control policy.',
      },
    },

    // ── Custom domain: section progress ───────────────────
    SectionProgress: {
      props: z.object({
        title: z.string().describe('Progress title'),
        overall: z.number().min(0).max(100).describe('Overall percentage'),
        currentSection: z.string().nullable().describe('Current section name'),
        currentQuestion: z.number().nullable().describe('Current question number'),
        totalQuestions: z.number().nullable().describe('Total questions in section'),
        sections: z.array(
          z.object({
            name: z.string(),
            status: z.enum(['complete', 'in-progress', 'pending']),
            score: z.string(),
          })
        ).describe('Section breakdown'),
      }),
      description: 'Shows assessment progress with overall bar and section breakdown.',
      example: {
        title: 'ISO 27001 Assessment',
        overall: 35,
        currentSection: 'Security Policies',
        sections: [
          { name: 'Organization Context', status: 'complete', score: '8/10' },
          { name: 'Security Policies', status: 'in-progress', score: '3/8' },
        ],
      },
    },

    // ── Custom domain: scorecard ──────────────────────────
    Scorecard: {
      props: z.object({
        title: z.string().describe('Scorecard title'),
        score: z.number().describe('Achieved score'),
        maxScore: z.number().nullable().describe('Maximum possible score (default 100)'),
        breakdown: z.array(
          z.object({
            label: z.string(),
            value: z.number(),
            color: z.enum(['success', 'warning', 'destructive']),
          })
        ).describe('Score breakdown items'),
        actions: z.array(
          z.object({
            label: z.string(),
            primary: z.boolean().nullable(),
          })
        ).nullable().describe('Action buttons'),
      }),
      events: ['select'],
      description: 'Displays a score card with progress bar, breakdown, and action buttons.',
      example: {
        title: 'Compliance Score',
        score: 72,
        maxScore: 100,
        breakdown: [
          { label: 'Compliant', value: 8, color: 'success' },
          { label: 'Partial', value: 4, color: 'warning' },
          { label: 'Non-Compliant', value: 2, color: 'destructive' },
        ],
      },
    },

    // ── Custom domain: summary grid ───────────────────────
    SummaryGrid: {
      props: z.object({
        title: z.string().describe('Summary title'),
        items: z.array(
          z.object({
            label: z.string(),
            value: z.string(),
          })
        ).describe('Key-value items'),
        footer: z.string().nullable().describe('Optional footer text'),
      }),
      description: 'Displays a key-value summary card with optional footer.',
      example: {
        title: 'Assessment Summary',
        items: [
          { label: 'Organization', value: 'Acme Corp' },
          { label: 'Framework', value: 'ISO 27001:2022' },
        ],
      },
    },

    // ── Custom domain: attachment list ────────────────────
    AttachmentList: {
      props: z.object({
        attachments: z.array(z.string()).describe('Array of file paths or URLs'),
      }),
      description: 'Displays file attachment cards with preview and download buttons.',
      example: {
        attachments: ['/workspace/report.pdf', 'https://example.com/file.docx'],
      },
    },

    // ── Custom domain: styled message ─────────────────────
    StyledMessage: {
      props: z.object({
        text: z.string().describe('Message text'),
        type: z.enum(['greeting', 'info', 'success', 'warning', 'transition'])
          .describe('Visual style variant'),
      }),
      description:
        'Displays a styled text card. Use "greeting" for welcomes, "info" for explanations, "success" for confirmations, "warning" for alerts, "transition" for section dividers.',
      example: { text: 'Welcome to the assessment!', type: 'greeting' },
    },

    // ── Custom domain: options list ───────────────────────
    OptionsList: {
      props: z.object({
        options: z.array(
          z.object({
            label: z.string(),
            description: z.string().nullable(),
            icon: z.string().nullable(),
          })
        ).describe('Available options'),
        allowFreeText: z.boolean().nullable().describe('Show hint for custom answer'),
      }),
      events: ['select'],
      description:
        'Presents clickable option buttons. Clicking sends the option label as a message.',
      example: {
        options: [
          { label: 'Start Assessment', description: 'Begin the compliance check' },
          { label: 'View Summary', description: 'See previous results' },
        ],
        allowFreeText: true,
      },
    },

    // ── Custom domain: action buttons ─────────────────────
    ActionButtons: {
      props: z.object({
        actions: z.array(
          z.object({
            label: z.string(),
            primary: z.boolean().nullable(),
          })
        ).describe('Action buttons'),
      }),
      events: ['select'],
      description: 'Displays a row of quick action buttons.',
      example: {
        actions: [
          { label: 'Continue', primary: true },
          { label: 'Skip' },
        ],
      },
    },

    // ── Custom domain: file upload zone ───────────────────
    FileUploadZone: {
      props: z.object({
        accept: z.array(z.string()).nullable().describe('Accepted file types'),
        maxSize: z.string().nullable().describe('Max file size display string'),
        hints: z.array(z.string()).nullable().describe('Suggested documents to upload'),
      }),
      events: ['upload'],
      description: 'Drag-and-drop file upload zone with hints.',
    },

    // ── Custom domain: channel selector ───────────────────
    ChannelSelector: {
      props: z.object({
        channels: z.array(z.string()).describe('Available channel IDs (email, slack, web, whatsapp)'),
      }),
      events: ['change'],
      description: 'Notification channel toggles with optional config inputs.',
    },

    // ── Custom domain: timeline view ──────────────────────
    TimelineView: {
      props: z.object({
        fields: z.array(
          z.object({
            name: z.string(),
            label: z.string(),
            type: z.enum(['text', 'date', 'select', 'email', 'number']),
            required: z.boolean().nullable(),
            placeholder: z.string().nullable(),
            options: z.array(
              z.object({ value: z.string(), label: z.string() })
            ).nullable(),
          })
        ).nullable().describe('Config fields for timeline'),
        autoGenerate: z.boolean().nullable().describe('Auto-generate milestones from target date'),
      }),
      events: ['change'],
      description: 'Timeline/milestone step with date inputs and generated milestones.',
    },

    // ── Dashboard: stat cards row ─────────────────────────
    StatCards: {
      props: z.object({
        cards: z.array(
          z.object({
            label: z.string(),
            value: z.union([z.string(), z.number()]),
            change: z.string().nullable().optional().describe('e.g. "+12%" or "-3"'),
            changeType: z.enum(['positive', 'negative', 'neutral']).nullable().optional(),
            icon: z.string().nullable().optional().describe('Lucide icon name'),
          })
        ).describe('KPI cards to display'),
      }),
      description: 'Row of KPI stat cards with optional change indicators.',
      example: {
        cards: [
          { label: 'Controls', value: 93, change: '+5', changeType: 'positive' },
          { label: 'Gaps', value: 7, changeType: 'negative' },
        ],
      },
    },

    // ── Dashboard: data table ───────────────────────────────
    DataTable: {
      props: z.object({
        columns: z.array(
          z.object({ key: z.string(), label: z.string() })
        ).describe('Column definitions'),
        rows: z.array(z.record(z.unknown())).describe('Row data'),
        emptyMessage: z.string().nullable().optional(),
      }),
      description: 'Sortable data table with column headers and rows.',
      example: {
        columns: [
          { key: 'name', label: 'Control' },
          { key: 'status', label: 'Status' },
        ],
        rows: [
          { name: 'Access Control', status: 'Implemented' },
        ],
      },
    },

    // ── Dashboard: checklist ────────────────────────────────
    Checklist: {
      props: z.object({
        title: z.string().describe('Checklist title'),
        items: z.array(
          z.object({
            id: z.string(),
            label: z.string(),
            checked: z.boolean(),
            description: z.string().nullable().optional(),
          })
        ).describe('Checklist items'),
      }),
      description: 'Task/action checklist with check marks.',
      example: {
        title: 'Implementation Tasks',
        items: [
          { id: '1', label: 'Define scope', checked: true },
          { id: '2', label: 'Risk assessment', checked: false },
        ],
      },
    },

    // ── Dashboard: progress bar ─────────────────────────────
    ProgressBar: {
      props: z.object({
        label: z.string().describe('Progress label'),
        value: z.number().min(0).max(100).describe('Progress percentage'),
        size: z.enum(['sm', 'md', 'lg']).nullable().optional(),
      }),
      description: 'Labeled progress bar indicator.',
      example: { label: 'Overall Compliance', value: 72 },
    },

    // ── Dashboard: activity feed ────────────────────────────
    ActivityFeed: {
      props: z.object({
        items: z.array(
          z.object({
            id: z.string(),
            title: z.string(),
            description: z.string().nullable().optional(),
            timestamp: z.string(),
            type: z.enum(['info', 'success', 'warning', 'error']).nullable().optional(),
          })
        ).describe('Activity items'),
      }),
      description: 'Recent activity timeline feed.',
      example: {
        items: [
          { id: '1', title: 'Gap analysis completed', timestamp: '2024-01-15T10:00:00Z', type: 'success' },
        ],
      },
    },

    // ── Dashboard: empty state ──────────────────────────────
    EmptyState: {
      props: z.object({
        title: z.string().describe('Empty state heading'),
        description: z.string().nullable().optional(),
        icon: z.string().nullable().optional().describe('Lucide icon name'),
        action: z.object({ label: z.string() }).nullable().optional(),
      }),
      description: 'Empty placeholder with optional call-to-action.',
      example: { title: 'No data yet', description: 'Start a conversation to generate data.' },
    },

    // ── Frozen legal / consent copy ───────────────────────
    LegalText: {
      props: z.object({
        key: z
          .string()
          .describe(
            'Key of a pre-approved legal string (e.g. "privacy_consent"). The wording is fixed in code — you choose WHICH string shows, never its text.'
          ),
        locale: z
          .string()
          .nullable()
          .optional()
          .describe('Locale for the string, e.g. "ar" or "en". Defaults to the session locale.'),
      }),
      description:
        'Renders a pre-approved, legally-reviewed string by key. Use for consent and privacy wording inside forms — never author that copy yourself.',
      example: { key: 'privacy_consent', locale: 'en' },
    },

    // ── Custom domain: multi-select list ──────────────────
    MultiSelectList: {
      props: z.object({
        options: z.array(
          z.object({
            value: z.string(),
            label: z.string(),
            description: z.string().nullable(),
          })
        ).describe('Selectable options'),
      }),
      events: ['change'],
      description: 'Checkbox list for selecting multiple items with descriptions.',
      example: {
        options: [
          { value: 'isms', label: 'ISMS Policy', description: 'Information Security Management' },
          { value: 'risk', label: 'Risk Assessment', description: 'Risk analysis framework' },
        ],
      },
    },
  },

  actions: {
    sendMessage: {
      params: z.object({
        text: z.string().describe('Message text to send as the user'),
      }),
      description: 'Send text as a user message in the chat',
    },
    submitStepData: {
      params: z.object({
        stepId: z.string().describe('Onboarding step ID'),
        data: z.record(z.unknown()).describe('Collected form data'),
      }),
      description: 'Submit collected data from an onboarding step',
    },
    uploadFiles: {
      params: z.object({
        files: z.array(z.unknown()).describe('Files to upload'),
      }),
      description: 'Handle file uploads',
    },
    /**
     * Structured form submit. Unlike `sendMessage` (which flattens values into
     * prose via `${/path}` interpolation), this returns the whole bound form
     * state to the agent as KEYED JSON, after client-side required-field
     * validation. Every named form input is already bound into the state store
     * by `addFormBindings`, so no per-field wiring is needed.
     */
    submitFormData: {
      params: z.object({
        formId: z
          .string()
          .describe('Stable id for this form, e.g. "pickup_request"'),
        required: z
          .array(z.string())
          .nullable()
          .optional()
          .describe(
            'Field names (the `name` prop of each input) that must be filled before submit is allowed'
          ),
        summary: z
          .string()
          .nullable()
          .optional()
          .describe('Short confirmation shown to the user in place of the raw values'),
      }),
      description:
        'Submit the form\'s collected values back to the agent as structured data. Use this — NOT sendMessage — on a form\'s submit button.',
    },
  },
})

export type TaqatCatalog = typeof taqatCatalog
